import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useSelector } from 'react-redux'

import Avatar from 'react-avatar'

import AuthAvatar from '../../../../UI/AuthAvatar'

import { IconSeal } from './icons'
import styles from './style.module.scss'
import { formatDate } from '../../../../shared/datetime'

/*
 * Die Abschlusskarte.
 *
 * Sie steht an zwei Stellen — am Ende der Schulung und im eigenen Profil — und
 * ist deshalb ein eigener Baustein. Zwei Fassungen desselben Dokuments liefen
 * beim nächsten Zusatz auseinander.
 *
 * **Sie zeigt nur den eigenen Stand.** Es gibt keine Route auf fremde Stände und
 * bewusst keine Rangliste: Die Abschlusskarte soll ein Anlass sein, die Schulung
 * einmal durchzugehen, und kein Grund, sich mit der Kollegin zu vergleichen.
 *
 * DAS PROFILBILD IST TEIL DER SACHE. Es steht links auf der Karte — und wer
 * keines hinterlegt hat, sieht auf seiner eigenen Abschlusskarte ein Feld mit
 * Initialen. Das ist die freundlichste Art, an die Praxisaufgabe zu erinnern,
 * die uns eingefallen ist.
 */

const KLASSE = {
	SOUVERAEN: { rand: styles.licenceGold, name: 'Souverän' },
	SICHER: { rand: styles.licenceSilver, name: 'Sicher' },
	BESTANDEN: { rand: styles.licenceBase, name: 'Bestanden' }
}

const datum = wert => formatDate(wert, { day: '2-digit', month: '2-digit', year: 'numeric' }) || null

export default function Licence({ stand, compact }) {
	const profil = useSelector(state => state.user.profile)

	const name = [profil?.titlePrefixed, profil?.firstName, profil?.lastName, profil?.titleSuffixed]
		.filter(Boolean)
		.join(' ')
		.trim()

	const level = stand?.level
	const klasse = (level && KLASSE[level.key]) || KLASSE.BESTANDEN
	const abgeschlossen = Boolean(stand?.completedAt)

	return (
		<div className={clsx(styles.licence, klasse.rand, compact && styles.licenceCompact)}>
			<div className={styles.licenceLeft}>
				<AuthAvatar
					hashName={profil?.avatarHashName}
					alt=""
					className={styles.licenceAvatar}
					fallback={
						<Avatar
							name={name || '?'}
							// Muss zu `.licenceAvatar` passen — react-avatar
							// zeichnet das Ersatzfeld selbst und kennt das CSS
							// nicht.
							size={compact ? '64' : '84'}
							round
							color="#c3edef"
							fgColor="#22404d"
						/>
					}
				/>
				<span className={styles.licenceSeal}>
					<IconSeal className={styles.licenceSealIcon} />
				</span>
			</div>

			<div className={styles.licenceMain}>
				<p className={styles.licenceKind}>Junior Datenbank · Onboarding</p>
				<h3 className={styles.licenceTitle}>Onboarding 2026/27</h3>
				<p className={styles.licenceName}>{name || profil?.email || '—'}</p>

				<dl className={styles.licenceFacts}>
					<div>
						<dt>Stufe</dt>
						<dd>{abgeschlossen ? klasse.name : 'noch offen'}</dd>
					</div>
					<div>
						<dt>Richtig</dt>
						<dd>
							{stand?.score ?? 0} von {stand?.maxScore ?? 0}
						</dd>
					</div>
					<div>
						<dt>Auf Anhieb</dt>
						<dd>{stand?.firstTry ?? 0}</dd>
					</div>
					<div>
						<dt>Joker</dt>
						<dd>{stand?.jokers ?? 0}</dd>
					</div>
					<div>
						<dt>Praxis</dt>
						<dd>{stand?.tasks ?? 0}</dd>
					</div>
					<div>
						<dt>Abgeschlossen</dt>
						<dd>{datum(stand?.completedAt) || '—'}</dd>
					</div>
				</dl>

				{abgeschlossen && level?.note && <p className={styles.licenceNote}>{level.note}</p>}
				{!abgeschlossen && (
					<p className={styles.licenceNote}>
						Das Onboarding ist noch nicht durch. Der Stand bleibt erhalten — weitermachen geht jederzeit.
					</p>
				)}
			</div>

			{/* Dezent, wie es sich gehört: klein, unten, ohne Farbe. */}
			<p className={styles.licenceFoot}>Junior Datenbank · Entwicklung und Betrieb: DaVinciLab</p>
		</div>
	)
}

Licence.propTypes = {
	stand: PropTypes.object,
	compact: PropTypes.bool
}
